import type { SofiaProposedAction } from "../model/types"
import { isRecord, readString } from "./actionArgumentReaders"
import { parseModifyActionArgs } from "./modifyActionArgs"

interface SummaryRow {
  key: string
  value: string
}

/** How many batch entries the card lists before collapsing the rest. */
const SHOWN_ENTRIES = 4

function entryCount(
  action: SofiaProposedAction,
  key: "test_cases" | "changes",
): number {
  const value = key === "changes" ? effectiveChanges(action) : action.args[key]
  return Array.isArray(value) ? value.length : 0
}

/** Use the same effective patches as confirmation and staging. Signed args stay untouched. */
function effectiveChanges(action: SofiaProposedAction) {
  const parsed = parseModifyActionArgs(action)
  return parsed?.ok && parsed.action.kind === "update_test_cases"
    ? parsed.action.args.changes
    : []
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`
}

function textField(entry: unknown, key: string): string | undefined {
  if (!isRecord(entry)) {
    return undefined
  }
  const value = entry[key]
  return typeof value === "string" ? value : undefined
}

function moreRow(total: number): SummaryRow[] {
  return total > SHOWN_ENTRIES
    ? [{ key: "more", value: String(total - SHOWN_ENTRIES) }]
    : []
}

/** "Draft 12 test cases" */
export function draftTestCasesTitle(action: SofiaProposedAction): string {
  return `Draft ${plural(entryCount(action, "test_cases"), "test case")}`
}

export function draftTestCasesMutationLabel(
  action: SofiaProposedAction,
): string {
  return `stageTestSetRows(${entryCount(action, "test_cases")} cases)`
}

/** "Update 22 test cases in ts_1" — one card for a set-wide edit. */
export function updateTestCasesTitle(action: SofiaProposedAction): string {
  const testSetId = readString(action.args, "test_set_id") ?? "?"
  return `Update ${plural(entryCount(action, "changes"), "test case")} in ${testSetId}`
}

export function updateTestCasesMutationLabel(
  action: SofiaProposedAction,
): string {
  return `stageTestCaseEdits(${entryCount(action, "changes")} cases)`
}

/**
 * The draft batch: its count, the requested total when it is larger, and the
 * first few case labels.
 */
export function summarizeDraftTestCasesArgs(
  action: SofiaProposedAction,
): SummaryRow[] {
  const cases = Array.isArray(action.args.test_cases)
    ? action.args.test_cases
    : []
  const requestedTotal = action.args.requested_total
  const totalRow =
    typeof requestedTotal === "number" &&
    Number.isInteger(requestedTotal) &&
    requestedTotal > cases.length
      ? [{ key: "requested total", value: String(requestedTotal) }]
      : []
  return [
    { key: "test cases", value: String(cases.length) },
    ...totalRow,
    ...cases.slice(0, SHOWN_ENTRIES).map((entry, index) => ({
      key: `case ${index + 1}`,
      value:
        textField(entry, "description") ??
        textField(entry, "input_str") ??
        "Test case",
    })),
    ...moreRow(cases.length),
  ]
}

/**
 * The edit batch: the set, the count, and which fields change on the first
 * few cases — enough to see the shape of a set-wide edit without 22 rows.
 */
export function summarizeUpdateTestCasesArgs(
  action: SofiaProposedAction,
): SummaryRow[] {
  const changes = effectiveChanges(action)
  return [
    { key: "test set", value: readString(action.args, "test_set_id") ?? "?" },
    { key: "test cases", value: String(changes.length) },
    ...changes.slice(0, SHOWN_ENTRIES).map((entry: unknown) => {
      const id = textField(entry, "test_case_id") ?? "?"
      const fields = isRecord(entry)
        ? Object.keys(entry).filter(
            (key) =>
              key !== "test_case_id" &&
              entry[key] !== undefined &&
              entry[key] !== null,
          )
        : []
      return {
        key: `case ${id}`,
        value:
          fields.length > 0
            ? fields
                .map((field) =>
                  field === "append_input_str" ? "input_str (append)" : field,
                )
                .join(", ")
            : "no change",
      }
    }),
    ...moreRow(changes.length),
  ]
}
